import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    List,
    ListItem,
    InputAdornment,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Snackbar,
    Alert,
    Tab,
    Tabs,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { getEmails, markEmailAsRead, getEmailLabels, searchEmails } from 'services/api/general/email';
import { initiateGoogleAuth, refreshGoogleToken } from 'services/api/user';
import EmailDetail from './EmailDetail';
import EmailLayout from './layout';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDAvatar from 'components/MDAvatar';

/**
 * E-posta listesi ve detay görünümünü sağlayan ana komponent
 * Gmail API ile entegre çalışır ve e-posta listesi ve seçilen e-postanın içeriğini gösterir
 */
const EmailList = () => {
    // State tanımlamaları
    const [selectedEmail, setSelectedEmail] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedLabel, setSelectedLabel] = useState('INBOX');
    const [page, setPage] = useState(1);
    const [nextPageToken, setNextPageToken] = useState(null);
    const [totalEmails, setTotalEmails] = useState(0);
    const [rowsPerPage] = useState(20);
    const [authAlert, setAuthAlert] = useState({ open: false, message: '', severity: 'info', action: null });
    const queryClient = useQueryClient();

    /**
     * Google bağlantısını kontrol eder ve gerekirse yeniler veya kullanıcıyı auth sayfasına yönlendirir
     * @returns {Promise<boolean>} Bağlantı durumunu döndürür
     */
    const checkAndRefreshGoogleAuth = async () => {
        try {
            const user = JSON.parse(localStorage.getItem('user'));
            console.log('Checking Google Auth status');

            // Hiç token yoksa direkt bağlantıya yönlendir, ama önce bilgi ver
            if (!user?.googleAuth?.accessToken && !user?.googleAuth?.refreshToken) {
                console.log('No Google Auth tokens found, initiating Google Auth');

                setAuthAlert({
                    open: true,
                    message: 'E-posta hizmetlerini kullanmak için Google hesabınıza bağlanmanız gerekiyor.',
                    severity: 'warning',
                    action: async () => {
                        const response = await initiateGoogleAuth();
                        if (response.data.success && response.data.result.authUrl) {
                            localStorage.setItem('last_auth_reason', 'İlk kez Google hesabına bağlanma');
                            window.location.href = response.data.result.authUrl;
                        }
                    },
                });

                return false;
            }

            // Token var ama süresi dolmuşsa yenilemeyi dene
            if (user?.googleAuth?.expiryDate) {
                const expiryDate = new Date(user.googleAuth.expiryDate);
                const now = new Date();

                console.log('Token Status:', {
                    expiryDate,
                    now,
                    isExpired: expiryDate <= now,
                    refreshToken: !!user?.googleAuth?.refreshToken,
                });

                // Süresi dolmadıysa geçerli token var demektir
                if (expiryDate > now) {
                    console.log('Token is still valid');
                    return true;
                }

                // Süresi dolmuşsa ve refresh token varsa, yenilemeyi dene
                if (user?.googleAuth?.refreshToken) {
                    console.log('Token expired, attempting to refresh');
                    try {
                        const updatedUser = await refreshGoogleToken(user);
                        if (updatedUser) {
                            console.log('Token refreshed successfully');
                            return true;
                        } else {
                            // Sadece refreshToken'in artık geçersiz olduğu kesin olarak anlaşıldığında yeni auth isteyelim
                            console.log('Token refresh failed, initiating Google Auth');

                            setAuthAlert({
                                open: true,
                                message:
                                    'Google oturum bilgileriniz geçersiz. E-posta hizmetlerini kullanmak için yeniden bağlanmanız gerekiyor.',
                                severity: 'warning',
                                action: async () => {
                                    const response = await initiateGoogleAuth();
                                    if (response.data.success && response.data.result.authUrl) {
                                        localStorage.setItem(
                                            'last_auth_reason',
                                            'Refresh token geçersiz olduğu için yeniden bağlanma'
                                        );
                                        window.location.href = response.data.result.authUrl;
                                    }
                                },
                            });

                            return false;
                        }
                    } catch (error) {
                        // Network hatası veya API hatası durumunda kullanıcıyı hemen yönlendirmeyelim
                        console.error('Token refresh error:', error);
                        if (error.response?.status === 400) {
                            setAuthAlert({
                                open: true,
                                message:
                                    'Google bağlantınız geçersiz hale gelmiş. E-posta hizmetlerini kullanmak için yeniden bağlanmanız gerekiyor.',
                                severity: 'warning',
                                action: async () => {
                                    const response = await initiateGoogleAuth();
                                    if (response.data.success && response.data.result.authUrl) {
                                        localStorage.setItem(
                                            'last_auth_reason',
                                            'HTTP 400: Refresh token geçersiz olduğu için yeniden bağlanma'
                                        );
                                        window.location.href = response.data.result.authUrl;
                                    }
                                },
                            });
                            return false;
                        }
                        // Diğer hatalarda (network hatası, geçici sunucu hatası gibi) sadece hata gösterelim
                        setAuthAlert({
                            open: true,
                            message: 'Google bağlantısında geçici bir hata oluştu. Lütfen daha sonra tekrar deneyin.',
                            severity: 'error',
                            action: null,
                        });
                        return false;
                    }
                }
            }

            console.log('No valid authentication data found');
            return false;
        } catch (error) {
            console.error('Error checking Google Auth:', error);
            setAuthAlert({
                open: true,
                message: 'Google Auth kontrolü sırasında bir hata oluştu.',
                severity: 'error',
                action: null,
            });
            return false;
        }
    };

    // Auth Alert'i kapatma ve onaylama işlevleri
    const handleCloseAuthAlert = () => {
        setAuthAlert({ ...authAlert, open: false });
    };

    const handleConfirmAuthAlert = () => {
        if (authAlert.action) {
            authAlert.action();
        }
        handleCloseAuthAlert();
    };

    // Sayfa yüklendiğinde Google bağlantısını kontrol et
    useEffect(() => {
        checkAndRefreshGoogleAuth();
    }, []);

    // Arama sorgusu değiştiğinde sayfa bilgilerini sıfırla
    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
        setPage(1);
        setNextPageToken(null);
    };

    /**
     * Etiket değiştiğinde sayfa bilgilerini sıfırla
     */
    const handleLabelChange = (event, newLabel) => {
        setSelectedLabel(newLabel);
        setPage(1);
        setNextPageToken(null);
        setSelectedEmail(null);
    };

    /**
     * React Query ile e-postaları getir
     * Arama sorgusu, seçilen etiket ve sayfa bilgisine göre e-postaları çeker
     */
    const {
        data: apiResponse = {},
        isLoading: emailsLoading,
        error: emailsError,
        refetch: refetchEmails,
    } = useQuery({
        queryKey: ['emails', selectedLabel, page, searchQuery],
        queryFn: async () => {
            // Her e-posta çekme işleminden önce auth kontrolü yap
            await checkAndRefreshGoogleAuth();

            const params = {
                label: selectedLabel,
                pageToken: page === 1 ? null : nextPageToken,
                limit: rowsPerPage,
            };

            // Arama sorgusu varsa search API'sini kullan, yoksa normal getEmails
            if (searchQuery && searchQuery.length >= 2) {
                params.query = searchQuery;
                return searchEmails(params);
            }

            return getEmails(params);
        },
        onSuccess: (data) => {
            // API yanıtından nextPageToken ve toplam e-posta sayısını al
            const responseData = data?.data?.result;
            if (responseData) {
                setNextPageToken(responseData.nextPageToken);
                setTotalEmails(responseData.resultSizeEstimate || responseData.messages?.length || 0);
            }
        },
        onError: (error) => {
            console.error('Query Error:', error);
            // API hatası auth kaynaklıysa bağlantıyı yenile
            if (error.response?.status === 401) {
                checkAndRefreshGoogleAuth();
            }
        },
        keepPreviousData: true,
        retry: 1,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    });

    /**
     * API yanıtından e-posta verilerini düzenli bir formata dönüştür
     * @param {Object} apiData - API yanıtı
     * @returns {Array} Düzenlenmiş e-posta listesi
     */
    const transformEmailData = (apiData) => {
        const messages = apiData?.data?.result?.messages;
        if (!messages) {
            return [];
        }

        return messages.map((email) => ({
            id: email.id || email.messageId,
            threadId: email.threadId,
            from: email.from || 'Unknown Sender',
            subject: email.subject || 'No Subject',
            snippet: email.snippet || '',
            date: email.date || new Date().toISOString(),
            read: email.read || false,
            label: email.label || selectedLabel,
            body: email.body || '',
            to: email.to || '',
            attachments: email.attachments || [],
        }));
    };

    // E-posta verilerini dönüştür
    const emails = transformEmailData(apiResponse);

    // Etiketleri getir
    const { data: labels = [], isLoading: labelsLoading } = useQuery({
        queryKey: ['emailLabels'],
        queryFn: getEmailLabels,
    });

    /**
     * E-postayı okundu olarak işaretle
     * Kullanıcı bir e-postaya tıkladığında otomatik olarak çağrılır
     */
    const markAsReadMutation = useMutation({
        mutationFn: ({ emailId, read }) => markEmailAsRead(emailId, read),
        onSuccess: (_, { emailId, read }) => {
            // İşlem başarılı olduğunda cache'i güncelle
            queryClient.setQueryData(
                {
                    queryKey: ['emails', selectedLabel, page],
                },
                (oldData) => {
                    if (!oldData?.data?.result?.messages) return oldData;

                    const newMessages = oldData.data.result.messages.map((email) =>
                        email.id === emailId || email.messageId === emailId ? { ...email, read } : email
                    );

                    return {
                        ...oldData,
                        data: {
                            ...oldData.data,
                            result: {
                                ...oldData.data.result,
                                messages: newMessages,
                            },
                        },
                    };
                }
            );
        },
    });

    /**
     * E-posta seçildiğinde çalışan fonksiyon
     * E-postayı seçer ve okunmamışsa okundu olarak işaretler
     */
    const handleEmailClick = async (email) => {
        setSelectedEmail(email);
        if (!email.read) {
            markAsReadMutation.mutate({ emailId: email.id, read: true });
            // Kullanıcı arayüzünü hemen güncelle
            setSelectedEmail({ ...email, read: true });
        }
    };

    /**
     * Tarih formatını düzenler
     * Bugün için saat, diğer günler için tarih gösterir
     */
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const today = new Date();

        if (date.toDateString() === today.toDateString()) {
            return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } else {
            return date.toLocaleDateString();
        }
    };

    // Sayfa değişikliği
    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    /**
     * Etiket için kullanıcı dostu başlık oluştur
     */
    const getLabelDisplayName = () => {
        switch (selectedLabel) {
            case 'SENT':
                return 'Gönderilen Postalar';
            case 'INBOX':
            default:
                return 'Gelen Kutusu';
        }
    };

    return (
        <EmailLayout selectedLabel={selectedLabel}>
            <MDBox display="flex" flex={1} overflow="hidden">
                {/* E-posta Listesi */}
                <MDBox
                    width="30%"
                    borderRight="1px solid"
                    borderColor="grey.200"
                    display="flex"
                    flexDirection="column"
                    bgColor="white"
                >
                    {/* Etiket Seçimi */}
                    <MDBox borderBottom="1px solid" borderColor="grey.100">
                        <Tabs
                            value={selectedLabel}
                            onChange={handleLabelChange}
                            variant="fullWidth"
                            textColor="primary"
                            indicatorColor="primary"
                        >
                            <Tab
                                value="INBOX"
                                label="Gelen Kutusu"
                                sx={[
                                    {
                                        textTransform: 'none',
                                        fontSize: '0.875rem',
                                    },
                                    selectedLabel === 'INBOX'
                                        ? {
                                              fontWeight: 'medium',
                                          }
                                        : {
                                              fontWeight: 'regular',
                                          },
                                ]}
                            />
                            <Tab
                                value="SENT"
                                label="Gönderilen"
                                sx={[
                                    {
                                        textTransform: 'none',
                                        fontSize: '0.875rem',
                                    },
                                    selectedLabel === 'SENT'
                                        ? {
                                              fontWeight: 'medium',
                                          }
                                        : {
                                              fontWeight: 'regular',
                                          },
                                ]}
                            />
                        </Tabs>
                    </MDBox>

                    {/* Arama Çubuğu */}
                    <MDBox p={1.5} borderBottom="1px solid" borderColor="grey.100">
                        <MDInput
                            fullWidth
                            placeholder="E-postaları ara..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: 'text.secondary' }} />
                                    </InputAdornment>
                                ),
                                sx: {
                                    height: '36px',
                                    '& .MuiInputBase-input': {
                                        py: 0.75,
                                    },
                                },
                            }}
                        />
                    </MDBox>

                    {/* E-posta Listesi İçeriği */}
                    {emailsLoading ? (
                        <MDBox display="flex" justifyContent="center" p={2}>
                            <CircularProgress size={24} sx={{ color: 'grey.500' }} />
                        </MDBox>
                    ) : emailsError ? (
                        <MDBox p={2} textAlign="center">
                            <MDTypography color="text" gutterBottom>
                                E-postalar yüklenirken bir hata oluştu
                            </MDTypography>
                            <MDTypography variant="button" color="text" gutterBottom>
                                {emailsError.message || 'Sunucu hatası'}
                            </MDTypography>
                            <MDButton color="dark" onClick={() => refetchEmails()} sx={{ mt: 2 }}>
                                Tekrar Dene
                            </MDButton>
                        </MDBox>
                    ) : emails.length === 0 ? (
                        <MDBox p={2} textAlign="center">
                            <MDTypography color="text">
                                {searchQuery ? 'Arama sonucu bulunamadı' : 'Bu klasörde e-posta bulunamadı'}
                            </MDTypography>
                        </MDBox>
                    ) : (
                        <MDBox display="flex" flexDirection="column" height="100%">
                            {/* E-posta Listesi */}
                            <List sx={{ overflow: 'auto', flex: 1, p: 0 }}>
                                {emails.map((email) => (
                                    <ListItem
                                        key={email.id}
                                        button
                                        selected={selectedEmail?.id === email.id}
                                        onClick={() => handleEmailClick(email)}
                                        sx={{
                                            py: 1.25,
                                            px: 2,
                                            borderBottom: '1px solid',
                                            borderColor: 'grey.100',
                                            bgcolor: email.read ? 'transparent' : 'grey.50',
                                            '&.Mui-selected': {
                                                bgcolor: 'grey.100',
                                                '&:hover': {
                                                    bgcolor: 'grey.100',
                                                },
                                            },
                                            '&:hover': {
                                                bgcolor: 'grey.50',
                                            },
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            width: '100%',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        {/* Gönderen Avatar */}
                                        <MDAvatar
                                            size="sm"
                                            sx={{
                                                mr: 2,
                                                bgcolor: email.read ? 'grey.400' : 'grey.700',
                                                color: 'white',
                                                flexShrink: 0,
                                                width: '32px',
                                                height: '32px',
                                            }}
                                        >
                                            {/[a-zA-Z]/.test(email.from?.charAt(0))
                                                ? email.from?.charAt(0).toUpperCase()
                                                : email.from?.charAt(1).toUpperCase()}
                                        </MDAvatar>

                                        {/* E-posta İçerik Önizleme */}
                                        <MDBox
                                            flex={1}
                                            minWidth={0}
                                            mr={2}
                                            sx={{
                                                overflow: 'hidden',
                                                maxWidth: 'calc(100% - 120px)',
                                            }}
                                        >
                                            <MDBox display="flex" alignItems="center" mb={0.5}>
                                                <MDTypography
                                                    variant="button"
                                                    fontWeight={email.read ? 'regular' : 'medium'}
                                                    textTransform="capitalize"
                                                    color={email.read ? 'text' : 'dark'}
                                                    noWrap
                                                >
                                                    {email.from}
                                                </MDTypography>
                                            </MDBox>
                                            <MDTypography
                                                variant="caption"
                                                fontWeight={email.read ? 'regular' : 'medium'}
                                                color={email.read ? 'text' : 'dark'}
                                                noWrap
                                            >
                                                {email.subject}
                                            </MDTypography>
                                            <MDTypography display="block" variant="caption" color="text" noWrap>
                                                {email.snippet}
                                            </MDTypography>
                                        </MDBox>

                                        {/* Tarih Bilgisi */}
                                        <MDBox
                                            width="80px"
                                            flexShrink={0}
                                            textAlign="right"
                                            sx={{
                                                position: 'relative',
                                                top: '2px',
                                            }}
                                        >
                                            <MDTypography variant="caption" color="text">
                                                {formatDate(email.date)}
                                            </MDTypography>
                                        </MDBox>
                                    </ListItem>
                                ))}
                            </List>

                            {/* Sayfalama Bölümü */}
                            <MDBox
                                p={2}
                                borderTop="1px solid"
                                borderColor="grey.200"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                bgcolor="white"
                            >
                                <MDTypography variant="caption" color="text">
                                    {`Toplam ${totalEmails} e-posta`}
                                </MDTypography>
                                <MDBox display="flex" gap={1}>
                                    <MDButton
                                        variant="text"
                                        color="dark"
                                        size="small"
                                        onClick={() => handlePageChange(null, page - 1)}
                                        disabled={page === 1 || emailsLoading}
                                    >
                                        Önceki
                                    </MDButton>
                                    <MDTypography variant="button" color="text">
                                        {`Sayfa ${page}`}
                                    </MDTypography>
                                    <MDButton
                                        variant="text"
                                        color="dark"
                                        size="small"
                                        onClick={() => handlePageChange(null, page + 1)}
                                        disabled={!nextPageToken || emailsLoading}
                                    >
                                        Sonraki
                                    </MDButton>
                                </MDBox>
                            </MDBox>
                        </MDBox>
                    )}
                </MDBox>

                {/* E-posta Detayı */}
                <MDBox flex={1} overflow="auto" bgColor="grey.50">
                    {selectedEmail ? (
                        <EmailDetail email={selectedEmail} />
                    ) : (
                        <MDBox
                            height="100%"
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            p={3}
                        >
                            <MDTypography variant="h6" color="text" mb={1}>
                                E-posta seçilmedi
                            </MDTypography>
                            <MDTypography variant="button" color="text">
                                Görüntülemek için bir e-posta seçin
                            </MDTypography>
                        </MDBox>
                    )}
                </MDBox>
            </MDBox>
            {/* Auth Alert Dialogs */}
            <Dialog open={authAlert.open && authAlert.action !== null} onClose={handleCloseAuthAlert}>
                <DialogTitle>Google Hesap Bağlantısı Gerekli</DialogTitle>
                <DialogContent>
                    <DialogContentText>{authAlert.message}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <MDButton onClick={handleCloseAuthAlert} color="secondary">
                        İptal
                    </MDButton>
                    <MDButton onClick={handleConfirmAuthAlert} color="primary">
                        Google'a Bağlan
                    </MDButton>
                </DialogActions>
            </Dialog>
            {/* Bilgi/Hata bildirimleri için Snackbar */}
            <Snackbar
                open={authAlert.open && authAlert.action === null}
                autoHideDuration={6000}
                onClose={handleCloseAuthAlert}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseAuthAlert} severity={authAlert.severity}>
                    {authAlert.message}
                </Alert>
            </Snackbar>
        </EmailLayout>
    );
};

export default EmailList;
